// src/pages/TripTracking.tsx
// Rider-facing trip tracking — NEVER shows driver earnings/profit
// Driver info fetched from users/{driverId}: firstName, profilePicture, contactNumber
// Falls back to initials avatar if no profilePicture
// Rebook → navigate("/Rebook") with full data including driver rateType from riders/{driverId}

import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { doc, getDoc, collection, query, where, getDocs, limit } from "firebase/firestore";
import { ref as dbRef, onValue, off } from "firebase/database";
import { db, rtdb } from "../../../firebase";
import type { RootState } from "../../../redux/store";
import {
  ChevronLeft, Navigation, Clock, Car, Flag,
  CheckCircle, XCircle, AlertCircle, RefreshCw,
  Star, RotateCcw, ArrowRight, Loader2, CreditCard,
  Radio, Phone, MessageCircle, ChevronUp, ChevronDown, Zap,
  Heart,
} from "lucide-react";
import rideCarImg from "../../../assets/ride/economy.png";
import appLogo from "../../../assets/app/logo.png";
import { RIDE_TYPES } from "../../../CATEGORIES/OperatorCategories";

/* ─── Types ──────────────────────────────────────────────────────────────── */
interface TripParams {
  tripId?: string; riderId?: string; driverId?: string; listingId?: string;
  pickupAddress?: string; dropoffAddress?: string;
  pickupLat?: number; pickupLng?: number;
  dropoffLat?: number; dropoffLng?: number;
  pricing?: Record<string, number>;
}
interface LivePos { lat: number; lng: number; isAvailable?: boolean; heading?: number; speed?: number; }

// From users/{driverId} — public profile only, no earnings
interface DriverUserDoc {
  firstName?: string; lastName?: string; fullName?: string;
  profilePicture?: string; profilePhoto?: string;
  contactNumber?: string; phone?: string;
}
// From Fleet collection (queried by driverId field) — vehicle / listing fields
interface DriverListingDoc {
  brand?: string; model?: string; licensePlate?: string;
  rating?: number; rate?: number; rateType?: string; currency?: string;
  fullName?: string; driverName?: string;
  rideType?: number | null;
}

interface PastTrip {
  tripId?: string; id?: string; status: string;
  pickupAddress?: string; dropoffAddress?: string;
  pickupLat?: number; pickupLng?: number;
  dropoffLat?: number; dropoffLng?: number;
  distanceKm?: number; totalCharged?: number;
  rateType?: string; createdAt?: any;
  driverId?: string; listingId?: string; rate?: number; currency?: string;
}

/* ─── Helpers ────────────────────────────────────────────────────────────── */
function haversineKm(la1: number, lo1: number, la2: number, lo2: number) {
  const R = 6371, dLa = (la2 - la1) * Math.PI / 180, dLo = (lo2 - lo1) * Math.PI / 180;
  const a = Math.sin(dLa / 2) ** 2 + Math.cos(la1 * Math.PI / 180) * Math.cos(la2 * Math.PI / 180) * Math.sin(dLo / 2) ** 2;
  return +(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))).toFixed(2);
}
function bearingDeg(la1: number, lo1: number, la2: number, lo2: number): number {
  const φ1 = la1 * Math.PI / 180, φ2 = la2 * Math.PI / 180, Δλ = (lo2 - lo1) * Math.PI / 180;
  return ((Math.atan2(Math.sin(Δλ) * Math.cos(φ2), Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ)) * 180 / Math.PI) + 360) % 360;
}
function fmtDate(ts: any): string {
  if (!ts) return "";
  const d = ts?.toDate ? ts.toDate() : new Date(ts?.seconds ? ts.seconds * 1000 : ts);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
function getInitials(name: string): string {
  if (!name) return "D";
  const parts = name.trim().split(" ");
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0][0].toUpperCase();
}

// Same RIDE_TYPES lookup RideBookings/DriverCars use — pulls the top-level
// ride-type image (car.png, bike.png…) for this driver's active vehicle.
function rideTypeItem(id: any): any {
  return RIDE_TYPES.find((r: any) => r.id === id);
}

/* ─── Status config ──────────────────────────────────────────────────────── */
const SC: Record<string, { label: string; headline: string; sub: string; color: string; bg: string; Icon: any }> = {
  requested:      { label: "Finding Driver",   headline: "Looking for your driver",          sub: "Matching you with the nearest available driver…",        color: "#D4900F", bg: "rgba(212,144,15,.08)",  Icon: Radio        },
  finding_driver: { label: "Searching",        headline: "Trying the next driver",           sub: "Expanding search — almost there…",                       color: "#D4900F", bg: "rgba(212,144,15,.08)",  Icon: RefreshCw    },
  accepted:       { label: "Confirmed",        headline: "Driver is on the way",             sub: "Your driver accepted and is heading to pick you up.",     color: "#1A9E6E", bg: "rgba(26,158,110,.08)",   Icon: CheckCircle  },
  arriving:       { label: "Arriving",         headline: "Driver is almost here",            sub: "Get ready — your driver is almost at the pickup!",        color: "#4A9EFF", bg: "rgba(74,158,255,.08)",   Icon: Navigation   },
  ongoing:        { label: "On Your Way",      headline: "Trip in progress",                 sub: "Sit back and relax. You're heading to your destination.", color: "#1A9E6E", bg: "rgba(26,158,110,.08)",   Icon: Zap          },
  completed:      { label: "Arrived!",         headline: "You've arrived!",                  sub: "Thanks for riding with Heartlas. Hope you enjoyed it!",   color: "#1A9E6E", bg: "rgba(26,158,110,.08)",   Icon: Flag         },
  cancelled:      { label: "Cancelled",        headline: "Ride was cancelled",               sub: "This ride has been cancelled.",                           color: "#C8202E", bg: "rgba(200,32,46,.08)",    Icon: XCircle      },
  no_driver:      { label: "No Driver",        headline: "No drivers found",                 sub: "No available drivers in your area. Please try again.",    color: "#D4900F", bg: "rgba(212,144,15,.08)",  Icon: AlertCircle  },
};

const CANCELLABLE = new Set(["requested", "finding_driver", "accepted", "arriving"]);
const TERMINAL    = new Set(["completed", "cancelled", "no_driver"]);
const SEARCHING   = new Set(["requested", "finding_driver"]);

/* ─── Map style — warm cream, matches Heartlas map tokens ────────────────── */
const LIGHT_MAP = [
  { elementType: "geometry",            stylers: [{ color: "#FBF6EE" }] },
  { elementType: "labels.text.stroke",  stylers: [{ color: "#FBF6EE" }] },
  { elementType: "labels.text.fill",    stylers: [{ color: "#8A7660" }] },
  { featureType: "road",         elementType: "geometry",        stylers: [{ color: "#ffffff" }] },
  { featureType: "road",         elementType: "geometry.stroke", stylers: [{ color: "#EEDFC4" }] },
  { featureType: "road.highway", elementType: "geometry",        stylers: [{ color: "#FCF6EA" }] },
  { featureType: "water",        elementType: "geometry",        stylers: [{ color: "#EAE2D3" }] },
  { featureType: "poi",    stylers: [{ visibility: "off" }] },
  { featureType: "transit",stylers: [{ visibility: "off" }] },
];

/* ─── Car icon ────────────────────────────────────────────────────────────
   Priority: the driver's ride-type image (same RIDE_TYPES `.image` field
   RideBookings/DriverCars read) → falls back to the bundled economy.png
   if no ride-type image is configured, or it fails to load. Both variants
   face SOUTH → rotate by (heading - 180). Cached per distinct URL. ────── */
const carImgCache = new Map<string, HTMLImageElement>();
function loadCarImg(url: string): Promise<HTMLImageElement> {
  const cached = carImgCache.get(url);
  if (cached?.complete) return Promise.resolve(cached);
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload  = () => { carImgCache.set(url, img); resolve(img); };
    img.onerror = () => { if (url !== rideCarImg) loadCarImg(rideCarImg).then(resolve); };
    img.src = url;
  });
}
loadCarImg(rideCarImg); // preload the default fallback immediately

function buildCarIcon(headingDeg: number, imgUrl: string, size = 64): string {
  const img = carImgCache.get(imgUrl) || carImgCache.get(rideCarImg);
  if (!img || !img.complete) return "";
  const canvas = document.createElement("canvas");
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  ctx.save();
  ctx.translate(size / 2, size / 2);
  ctx.rotate(((headingDeg - 180) * Math.PI) / 180);
  ctx.drawImage(img, -size / 2, -size / 2, size, size);
  ctx.restore();
  return canvas.toDataURL();
}


const mkPickup  = () => "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' width='28' height='28'><circle cx='14' cy='14' r='11' fill='%231B0E06' stroke='white' stroke-width='3'/><circle cx='14' cy='14' r='4.5' fill='white'/></svg>`);
const mkDropoff = () => "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' width='34' height='44' viewBox='0 0 34 44'><path d='M17 0C7.6 0 0 7.6 0 17c0 14 17 27 17 27S34 31 34 17C34 7.6 26.4 0 17 0z' fill='%23C8202E'/><circle cx='17' cy='17' r='8.5' fill='white'/><circle cx='17' cy='17' r='4' fill='%23C8202E'/></svg>`);

const LERP_STEPS = 30, LERP_MS = 16;

/* ─── Status progress steps ──────────────────────────────────────────────── */
const STATUS_STEPS = [
  { key: "requested", label: "Request Sent",     sub: "Searching for a driver" },
  { key: "accepted",  label: "Driver Confirmed", sub: "Heading to pickup" },
  { key: "arriving",  label: "Driver Arriving",  sub: "Almost at your location" },
  { key: "ongoing",   label: "Ride in Progress", sub: "On the way to destination" },
  { key: "completed", label: "Arrived!",          sub: "Trip complete" },
];
const STEP_ORDER = STATUS_STEPS.map(s => s.key);
function getStepState(key: string, cur: string): "done" | "active" | "pending" {
  const si = STEP_ORDER.indexOf(key), ci = STEP_ORDER.indexOf(cur);
  return si < ci ? "done" : si === ci ? "active" : "pending";
}

/* ─── CSS — Heartlas gold/red/ivory brand theme (matches BookRide tokens) ── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;0,900;1,400;1,600&family=Syne:wght@400;500;600;700;800&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
:root{
  --red:#C8202E;--red2:#9E1521;
  --blk:#1B0E06;--wht:#ffffff;--off:#FBF6EE;--off2:#F5EEDD;
  --bord:#EEDFC4;--muted:#C9B79B;--smoke:#8A7660;
  --grn:#1A9E6E;--blu:#4A9EFF;--gld:#D4900F;
  --grad:linear-gradient(135deg,#1B0E06 0%,#D4900F 35%,#C8202E 65%,#F2A93E 100%);
  --serif:'Playfair Display',Georgia,serif;
  --sans:'Syne',-apple-system,sans-serif;
  --sr:26px;
}
@keyframes ttGradSpin{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
html,body{height:100%;overflow:hidden;background:var(--off);}
.tt{width:100vw;height:100dvh;font-family:var(--sans);color:var(--blk);display:flex;flex-direction:column;overflow:hidden;position:relative;}
.tt-hdr{position:absolute;top:0;left:0;right:0;z-index:60;display:flex;align-items:center;height:58px;padding:0 16px;gap:12px;background:rgba(255,255,255,.86);backdrop-filter:blur(20px);-webkit-backdrop-filter:blur(20px);border-bottom:1px solid rgba(27,14,6,.08);}
.tt-logo{display:flex;align-items:center;gap:8px;cursor:pointer;}
.tt-logo-sq{width:30px;height:30px;background:var(--grad);background-size:200% 100%;animation:ttGradSpin 4s ease infinite;display:flex;align-items:center;justify-content:center;border-radius:9px;flex-shrink:0;}
.tt-logo-name{font-family:var(--serif);font-size:16px;font-weight:800;color:var(--blk);letter-spacing:.5px;}
.tt-hdiv{width:1px;height:20px;background:var(--bord);}
.tt-hdr-title{font-size:10px;font-weight:700;letter-spacing:3px;color:var(--smoke);text-transform:uppercase;flex:1;}
.tt-live-pill{display:flex;align-items:center;gap:5px;background:rgba(26,158,110,.1);border:1px solid rgba(26,158,110,.2);padding:4px 10px;border-radius:20px;}
.tt-live-dot{width:6px;height:6px;border-radius:50%;background:var(--grn);animation:ttBlink 1.4s ease-in-out infinite;}
.tt-live-txt{font-size:9px;font-weight:700;letter-spacing:2px;color:var(--grn);}
.tt-icon-btn{width:33px;height:33px;border:1px solid var(--bord);background:var(--wht);display:flex;align-items:center;justify-content:center;cursor:pointer;color:var(--blk);border-radius:9px;transition:background .15s;box-shadow:0 1px 3px rgba(27,14,6,.06);}
.tt-icon-btn:hover{background:var(--off);}
.tt-tabbar{position:absolute;top:58px;left:0;right:0;z-index:55;display:flex;background:rgba(255,255,255,.9);backdrop-filter:blur(16px);border-bottom:1px solid rgba(27,14,6,.05);}
.tt-tab{flex:1;display:flex;align-items:center;justify-content:center;gap:6px;padding:11px 0;cursor:pointer;background:none;border:none;border-bottom:3px solid transparent;font-family:var(--sans);color:var(--muted);font-size:10px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;transition:color .2s,border-color .2s;margin-bottom:-1px;}
.tt-tab:hover{color:var(--smoke);}
.tt-tab-on{color:var(--blk);border-bottom-color:var(--red);}
.tt-tab-pip{width:6px;height:6px;border-radius:50%;background:var(--red);animation:ttBlink 1.4s infinite;}
#tt-gmap{position:absolute;inset:0;width:100%;height:100%;}
.tt-searching-ov{position:absolute;inset:0;z-index:20;pointer-events:none;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:18px;background:rgba(251,246,238,.78);backdrop-filter:blur(8px);}
.tt-srch-orb{width:88px;height:88px;border-radius:50%;background:rgba(200,32,46,.06);border:1.5px solid rgba(200,32,46,.18);display:flex;align-items:center;justify-content:center;color:var(--red);animation:ttPulse 1.8s ease-in-out infinite;position:relative;}
.tt-srch-orb::before{content:'';position:absolute;inset:-14px;border-radius:50%;border:1px solid rgba(200,32,46,.1);animation:ttPulse 1.8s ease-in-out infinite .3s;}
.tt-srch-orb::after{content:'';position:absolute;inset:-28px;border-radius:50%;border:1px solid rgba(200,32,46,.05);animation:ttPulse 1.8s ease-in-out infinite .6s;}
.tt-srch-lbl{font-family:var(--serif);font-size:14px;font-weight:700;color:var(--blk);letter-spacing:2px;text-transform:uppercase;}
.tt-srch-sub{font-size:12px;color:var(--smoke);text-align:center;max-width:220px;line-height:1.6;}
.tt-sheet{position:absolute;bottom:0;left:0;right:0;z-index:40;background:var(--wht);border-radius:var(--sr) var(--sr) 0 0;box-shadow:0 -2px 6px rgba(27,14,6,.04),0 -8px 28px rgba(27,14,6,.08),0 -28px 72px rgba(27,14,6,.07);border-top:1px solid rgba(255,255,255,.7);transition:transform .44s cubic-bezier(.22,1,.36,1);display:flex;flex-direction:column;max-height:90dvh;will-change:transform;}
.tt-sheet.peek{transform:translateY(calc(100% - 112px));}
.tt-sheet.half{transform:translateY(calc(100% - 50dvh));}
.tt-sheet.full{transform:translateY(0);}
.tt-sh-handle{flex-shrink:0;display:flex;justify-content:center;padding:12px 0 8px;cursor:pointer;}
.tt-sh-pill{width:38px;height:4px;background:var(--off2);border-radius:2px;}
.tt-sh-status{flex-shrink:0;display:flex;align-items:center;gap:14px;padding:10px 20px 14px;border-bottom:1px solid var(--off2);}
.tt-sh-icon{width:46px;height:46px;border-radius:14px;display:flex;align-items:center;justify-content:center;flex-shrink:0;box-shadow:0 2px 8px rgba(27,14,6,.08),inset 0 1px 0 rgba(255,255,255,.7);}
.tt-sh-text{flex:1;min-width:0;}
.tt-sh-headline{font-family:var(--serif);font-size:17px;font-weight:800;color:var(--blk);line-height:1.2;}
.tt-sh-sub{font-size:10px;color:var(--smoke);margin-top:2px;line-height:1.5;}
.tt-eta{flex-shrink:0;display:flex;flex-direction:column;align-items:center;background:var(--blk);color:var(--wht);padding:7px 13px;border-radius:12px;box-shadow:0 4px 14px rgba(27,14,6,.18);}
.tt-eta-num{font-family:var(--serif);font-size:22px;font-weight:800;line-height:1;}
.tt-eta-unit{font-size:8px;font-weight:700;letter-spacing:2px;margin-top:1px;opacity:.65;}
.tt-sh-tog{margin-left:6px;cursor:pointer;color:var(--muted);flex-shrink:0;}
.tt-sh-body{flex:1;overflow-y:auto;padding:8px 20px 40px;}
.tt-sh-body::-webkit-scrollbar{width:0;}
.tt-dashes{display:flex;gap:5px;padding:14px 0 12px;}
.tt-dash{height:4px;flex:1;border-radius:2px;background:var(--off2);transition:background .4s,transform .3s;}
.tt-dash.done{background:var(--blk);}
.tt-dash.active{background:var(--red);transform:scaleY(1.6);transform-origin:center;}
.tt-narr{margin-bottom:16px;}
.tt-narr-step{display:flex;align-items:flex-start;gap:12px;padding:8px 0;position:relative;}
.tt-narr-step:not(:last-child)::before{content:'';position:absolute;left:14px;top:32px;bottom:-8px;width:1px;background:var(--off2);}
.tt-narr-dot{width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;border:1.5px solid;box-shadow:0 2px 6px rgba(27,14,6,.06);}
.tt-narr-dot.done{background:var(--blk);border-color:var(--blk);color:#fff;}
.tt-narr-dot.active{background:var(--red);border-color:var(--red);color:#fff;animation:ttGlow 1.6s ease-in-out infinite;}
.tt-narr-dot.pending{background:var(--wht);border-color:var(--bord);color:var(--muted);}
.tt-narr-label{font-size:13px;font-weight:700;color:var(--blk);padding-top:4px;}
.tt-narr-label.pending{color:var(--muted);font-weight:500;}
.tt-narr-sub{font-size:11px;color:var(--smoke);margin-top:2px;line-height:1.5;}
/* driver card — rider facing, NO earnings ever */
.tt-drv{background:linear-gradient(145deg,#1B0E06,#2c1a0d);color:var(--wht);border-radius:18px;padding:16px;margin-bottom:14px;position:relative;overflow:hidden;box-shadow:0 4px 20px rgba(27,14,6,.22),inset 0 1px 0 rgba(255,255,255,.05);}
.tt-drv::before{content:'';position:absolute;top:-50px;right:-50px;width:150px;height:150px;border-radius:50%;background:rgba(242,169,62,.12);pointer-events:none;}
.tt-drv-row{display:flex;align-items:center;gap:12px;position:relative;}
.tt-drv-av-wrap{position:relative;flex-shrink:0;}
.tt-drv-av{width:52px;height:52px;border-radius:50%;border:2px solid rgba(255,255,255,.12);object-fit:cover;}
.tt-drv-av-fb{width:52px;height:52px;border-radius:50%;background:rgba(242,169,62,.22);border:2px solid rgba(242,169,62,.4);display:flex;align-items:center;justify-content:center;font-family:var(--serif);font-size:19px;font-weight:800;color:var(--wht);flex-shrink:0;}
.tt-drv-online{position:absolute;bottom:1px;right:1px;width:12px;height:12px;border-radius:50%;background:#4ade80;border:2px solid #1c1c1c;}
.tt-drv-info{flex:1;min-width:0;}
.tt-drv-name{font-family:var(--serif);font-size:16px;font-weight:800;color:var(--wht);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.tt-drv-veh{font-size:11px;color:rgba(255,255,255,.5);margin-top:3px;}
.tt-drv-plate{font-size:10px;font-weight:700;letter-spacing:3px;color:#F2A93E;margin-top:3px;}
.tt-drv-star{display:flex;align-items:center;gap:4px;background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.1);padding:5px 9px;border-radius:9px;}
.tt-drv-star span{font-size:12px;font-weight:700;color:var(--wht);}
.tt-drv-acts{display:flex;gap:8px;margin-top:12px;position:relative;}
.tt-drv-act{flex:1;display:flex;align-items:center;justify-content:center;gap:6px;padding:10px 0;border-radius:11px;border:none;cursor:pointer;font-size:11px;font-weight:700;font-family:var(--sans);transition:all .15s;letter-spacing:.3px;}
.tt-drv-act.ph{background:rgba(255,255,255,.09);color:rgba(255,255,255,.85);}
.tt-drv-act.ph:hover{background:rgba(255,255,255,.15);transform:translateY(-1px);}
.tt-drv-act.ms{background:var(--red);color:#fff;}
.tt-drv-act.ms:hover{background:var(--red2);transform:translateY(-1px);box-shadow:0 4px 12px rgba(200,32,46,.4);}
.tt-route{background:var(--off);border:1px solid var(--bord);border-radius:14px;padding:13px 15px;margin-bottom:14px;}
.tt-route-row{display:flex;align-items:flex-start;gap:10px;}
.tt-route-dots{display:flex;flex-direction:column;align-items:center;flex-shrink:0;padding-top:3px;}
.tt-route-dot{width:9px;height:9px;border-radius:50%;border:2px solid;}
.tt-route-vline{width:1px;height:18px;background:var(--bord);margin:3px 0;}
.tt-route-body{flex:1;min-width:0;}
.tt-route-lbl{font-size:8px;font-weight:700;letter-spacing:1.5px;color:var(--muted);text-transform:uppercase;margin-bottom:2px;}
.tt-route-addr{font-size:12px;font-weight:600;color:var(--blk);line-height:1.35;}
/* price strip — total + service fee ONLY */
.tt-price{display:flex;border:1px solid var(--bord);border-radius:14px;overflow:hidden;margin-bottom:14px;background:var(--wht);box-shadow:0 1px 4px rgba(27,14,6,.04);}
.tt-pi-item{flex:1;display:flex;flex-direction:column;align-items:center;padding:13px 8px;}
.tt-pi-lbl{font-size:8px;font-weight:700;letter-spacing:2px;color:var(--muted);text-transform:uppercase;margin-bottom:4px;}
.tt-pi-val{font-family:var(--serif);font-size:16px;font-weight:700;color:var(--blk);}
.tt-pi-div{width:1px;background:var(--bord);}
.tt-cancel{display:flex;align-items:center;justify-content:center;gap:7px;width:100%;padding:13px 0;background:none;border:1.5px solid var(--red);border-radius:13px;cursor:pointer;color:var(--red);font-size:11px;font-weight:700;font-family:var(--sans);letter-spacing:.05em;text-transform:uppercase;transition:all .15s;margin-bottom:10px;}
.tt-cancel:hover:not(:disabled){background:rgba(200,32,46,.04);transform:translateY(-1px);}
.tt-cancel:disabled{opacity:.5;cursor:not-allowed;}
.tt-done-row{display:flex;gap:9px;}
.tt-done-btn{flex:1;display:flex;align-items:center;justify-content:center;gap:7px;padding:14px 0;background:var(--grad);background-size:200% 100%;animation:ttGradSpin 6s ease infinite;border:none;border-radius:13px;color:var(--wht);font-size:11px;font-weight:700;letter-spacing:2px;text-transform:uppercase;cursor:pointer;font-family:var(--sans);transition:opacity .2s;box-shadow:0 4px 14px rgba(27,14,6,.14);}
.tt-done-btn:hover{filter:brightness(1.08);}
.tt-hist-btn{display:flex;align-items:center;gap:5px;padding:14px 15px;border:1.5px solid var(--bord);border-radius:13px;background:var(--wht);cursor:pointer;color:var(--smoke);font-size:11px;font-weight:600;font-family:var(--sans);transition:background .15s;}
.tt-hist-btn:hover{background:var(--off);}
@media(min-width:900px){
  .tt-sheet{display:none!important;}
  .tt-sidebar{position:absolute;right:0;top:98px;bottom:0;width:390px;z-index:30;background:var(--wht);border-left:1px solid var(--bord);display:flex;flex-direction:column;overflow:hidden;box-shadow:-6px 0 28px rgba(27,14,6,.06);}
  .tt-sb-status{flex-shrink:0;padding:18px 22px 14px;border-bottom:1px solid var(--off2);}
  .tt-sb-status-row{display:flex;align-items:center;gap:12px;}
  .tt-sb-icon{width:46px;height:46px;border-radius:14px;display:flex;align-items:center;justify-content:center;flex-shrink:0;box-shadow:0 2px 8px rgba(27,14,6,.08);}
  .tt-sb-body{flex:1;overflow-y:auto;padding:14px 22px 48px;}
  .tt-sb-body::-webkit-scrollbar{width:0;}
}
@media(max-width:899px){.tt-sidebar{display:none!important;}}
.tt-empty-screen{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:48px 32px;text-align:center;gap:12px;background:var(--wht);}
.tt-empty-title{font-family:var(--serif);font-size:30px;font-weight:800;color:var(--blk);}
.tt-empty-sub{font-size:13px;color:var(--muted);line-height:1.7;max-width:260px;}
.tt-book-btn{display:flex;align-items:center;gap:8px;padding:14px 30px;background:var(--grad);background-size:200% 100%;animation:ttGradSpin 6s ease infinite;border:none;color:var(--wht);font-size:11px;font-weight:700;letter-spacing:2px;text-transform:uppercase;cursor:pointer;font-family:var(--sans);border-radius:13px;margin-top:10px;box-shadow:0 4px 16px rgba(200,32,46,.3);}
.tt-book-btn:hover{filter:brightness(1.08);}
.tt-past-screen{position:absolute;inset:98px 0 0 0;overflow-y:auto;background:var(--off);}
.tt-past-screen::-webkit-scrollbar{width:0;}
.tt-past-list{padding:0 18px 80px;}
.tt-past-hdr{padding:22px 0 14px;border-bottom:1.5px solid var(--blk);margin-bottom:14px;}
.tt-past-title{font-family:var(--serif);font-size:28px;font-weight:800;color:var(--blk);}
.tt-past-sub{font-size:11px;color:var(--muted);margin-top:2px;}
.tt-trip-card{background:var(--wht);border-radius:16px;margin-bottom:9px;overflow:hidden;box-shadow:0 2px 8px rgba(27,14,6,.04),0 6px 20px rgba(27,14,6,.04);border:1px solid var(--bord);opacity:0;transform:translateY(10px);animation:ttUp .3s ease var(--d,0s) both;transition:box-shadow .18s,transform .18s;}
.tt-trip-card:hover{transform:translateY(-2px);box-shadow:0 4px 16px rgba(27,14,6,.07),0 10px 28px rgba(27,14,6,.06);}
.tt-trip-accent{height:4px;}
.tt-trip-body{padding:14px 16px;}
.tt-trip-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:10px;gap:6px;}
.tt-trip-pill{display:flex;align-items:center;gap:5px;border:1.5px solid;padding:3px 9px;font-size:9px;font-weight:700;letter-spacing:.5px;text-transform:uppercase;border-radius:20px;}
.tt-trip-date{font-size:10px;color:var(--muted);flex-shrink:0;}
.tt-trip-route{background:var(--off);border:1px solid var(--bord);border-radius:11px;padding:10px 12px;margin-bottom:10px;}
.tt-trip-route-row{display:flex;align-items:flex-start;gap:9px;}
.tt-trip-route-row+.tt-trip-route-row{margin-top:6px;}
.tt-trip-route-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0;margin-top:3px;}
.tt-trip-route-body{flex:1;min-width:0;}
.tt-trip-route-lbl{font-size:7px;font-weight:700;letter-spacing:1.5px;color:var(--muted);text-transform:uppercase;margin-bottom:1px;}
.tt-trip-route-addr{font-size:12px;font-weight:600;color:var(--blk);line-height:1.3;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.tt-trip-stats{display:flex;flex-wrap:wrap;gap:7px;margin-top:2px;}
.tt-trip-stat{display:flex;align-items:center;gap:4px;font-size:11px;font-weight:600;color:var(--smoke);background:var(--off);padding:4px 8px;border-radius:7px;}
.tt-rebook{display:flex;align-items:center;gap:7px;padding:9px 16px;background:var(--blk);border:none;border-radius:10px;color:var(--wht);font-size:10px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;cursor:pointer;font-family:var(--sans);margin-top:10px;transition:all .2s;box-shadow:0 2px 8px rgba(27,14,6,.15);}
.tt-rebook:hover:not(:disabled){background:#2c1a0d;transform:translateY(-1px);box-shadow:0 4px 14px rgba(27,14,6,.22);}
.tt-rebook:disabled{opacity:.5;cursor:not-allowed;transform:none;}
.tt-loader{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;padding:64px 32px;}
.tt-loader-sp{width:26px;height:26px;border-radius:50%;border:2px solid var(--bord);border-top-color:var(--red);animation:ttSpin .8s linear infinite;}
.tt-toast{position:fixed;bottom:28px;left:50%;transform:translateX(-50%) translateY(20px);background:var(--blk);color:var(--wht);padding:13px 22px;display:flex;align-items:center;gap:10px;font-size:13px;font-weight:700;z-index:300;opacity:0;pointer-events:none;transition:opacity .38s,transform .38s;white-space:nowrap;border-radius:16px;box-shadow:0 8px 32px rgba(27,14,6,.25);border-left:4px solid var(--grn);}
.tt-toast.show{opacity:1;transform:translateX(-50%) translateY(0);}
.spin{animation:ttSpin .8s linear infinite;}
@keyframes ttSpin  {to{transform:rotate(360deg);}}
@keyframes ttBlink {0%,100%{opacity:.4;}50%{opacity:1;}}
@keyframes ttPulse {0%,100%{transform:scale(1);opacity:.8;}50%{transform:scale(1.07);opacity:1;}}
@keyframes ttGlow  {0%,100%{box-shadow:0 0 0 0 rgba(200,32,46,.5);}50%{box-shadow:0 0 0 8px rgba(200,32,46,0);}}
@keyframes ttUp    {to{opacity:1;transform:translateY(0);}}
::-webkit-scrollbar{width:0;height:0;}
`;

/* ═══════════════════════════════════════════════════════════════════════════ */
export default function TripTracking() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user }  = useSelector((s: RootState) => s.auth);

  const params: TripParams = (location.state as TripParams) || {};
  const {
    tripId: initTripId, driverId: initDriverId,
    pickupAddress: initPickupAddress = "",
    dropoffAddress: initDropoffAddress = "",
    pickupLat: initPickupLat, pickupLng: initPickupLng,
    dropoffLat: initDropoffLat, dropoffLng: initDropoffLng,
    pricing: initPricing = {},
  } = params;

  const { userId: userIdParam } = useParams<{ userId: string }>();
  const riderId = userIdParam || params.riderId || (user as any)?.userId || (user as any)?.uid;

  /* map refs */
  const [mapReady,      setMapReady]     = useState(typeof (window as any).google !== "undefined");
  const mapRef         = useRef<any>(null);
  const carMarkerRef   = useRef<any>(null);
  const pickupMarkRef  = useRef<any>(null);
  const dropMarkRef    = useRef<any>(null);
  const polylinesRef   = useRef<any[]>([]);
  const lerpRef        = useRef<ReturnType<typeof setInterval> | null>(null);
  const displayPosRef  = useRef<{ lat: number; lng: number } | null>(null);
  const prevGPSRef     = useRef<{ lat: number; lng: number } | null>(null);
  const prevHeadingRef = useRef<number>(0);
  const routePathRef    = useRef<any[]>([]);
const carPctRef       = useRef<number>(0);
const carAnimFrameRef = useRef<number | null>(null);
const directionsRendererRef = useRef<any>(null);
const lastRouteKeyRef       = useRef<string>("");
  /* core state */
  const [tab,          setTab]         = useState<"active" | "past">("past");
  const [tripId,       setTripId]      = useState<string | null>(initTripId || null);
  const [driverId,     setDriverId]    = useState<string | null>(initDriverId || null);
  const [tripStatus,   setTripStatus]  = useState("requested");
  const [etaMinutes,   setEtaMinutes]  = useState<number | null>(null);
  const [driverPos,    setDriverPos]   = useState<LivePos | null>(null);

  /* driver info — from TWO separate collections */
  const [driverUserDoc,    setDriverUserDoc]    = useState<DriverUserDoc | null>(null);    // users/{driverId}
  const [driverListingDoc, setDriverListingDoc] = useState<DriverListingDoc | null>(null); // Fleet doc for this driverId

  // Ride-type image for the map marker — falls back to the bundled
  // economy.png when this driver has no ride-type image configured.
  const carImgUrl = rideTypeItem(driverListingDoc?.rideType)?.image || rideCarImg;
  const carImgUrlRef = useRef(carImgUrl);
  useEffect(() => { carImgUrlRef.current = carImgUrl; loadCarImg(carImgUrl); }, [carImgUrl]);

  const [cancelling,   setCancelling]  = useState(false);
  const [pastTrips,    setPastTrips]   = useState<PastTrip[]>([]);
  const [loadingPast,  setLoadingPast] = useState(false);
  const [rebookingId,  setRebookingId] = useState<string | null>(null);
  const [pricing,      setPricing]     = useState<Record<string, number>>(initPricing as Record<string, number>);
  const [sheetMode,    setSheetMode]   = useState<"peek" | "half" | "full">("half");
  const [showToast,    setShowToast]   = useState(false);
  const [initializing, setInitializing]= useState(true);

  const [pickupAddress,  setPickupAddress]  = useState(initPickupAddress);
  const [dropoffAddress, setDropoffAddress] = useState(initDropoffAddress);
  const [pickupLat,  setPickupLat]  = useState<number | undefined>(initPickupLat);
  const [pickupLng,  setPickupLng]  = useState<number | undefined>(initPickupLng);
  const [dropoffLat, setDropoffLat] = useState<number | undefined>(initDropoffLat);
  const [dropoffLng, setDropoffLng] = useState<number | undefined>(initDropoffLng);
  const prevStatusRef = useRef(tripStatus);

  /* CSS injection */
  useEffect(() => {
    const id = "tt-css-v14-heartlas";
    if (!document.getElementById(id)) {
      const t = document.createElement("style"); t.id = id; t.textContent = CSS;
      document.head.appendChild(t);
    }
  }, []);

  /* Google Maps ready poll */
  useEffect(() => {
    if (mapReady) return;
    const t = setInterval(() => {
      if (typeof (window as any).google !== "undefined") { setMapReady(true); clearInterval(t); }
    }, 100);
    return () => clearInterval(t);
  }, [mapReady]);

  /* ── Mount: look for active trip; fall back to history ── */
  useEffect(() => {
    if (!riderId) { setInitializing(false); return; }
    if (initTripId) { setTab("active"); setInitializing(false); return; }

    const ACTIVE = ["requested", "finding_driver", "accepted", "arriving", "ongoing"];
    import("firebase/firestore").then(({ collection, query, where, getDocs, limit }) => {
      const q = query(
        collection(db, "rideTrips"),
        where("riderId", "==", riderId),
        where("status", "in", ACTIVE),
        limit(5)
      );
      getDocs(q).then(snap => {
        if (!snap.empty) {
          const sorted = snap.docs.slice().sort((a, b) =>
            (b.data()?.createdAt?.seconds ?? 0) - (a.data()?.createdAt?.seconds ?? 0)
          );
          const d = sorted[0].data() as any;
          setTripId(d.tripId || sorted[0].id);
          setDriverId(d.driverId || null);
          setTripStatus(d.status || "requested");
          if (d.etaMinutes)     setEtaMinutes(d.etaMinutes);
          if (d.pickupAddress)  setPickupAddress(d.pickupAddress);
          if (d.dropoffAddress) setDropoffAddress(d.dropoffAddress);
          if (d.pickupLat)      setPickupLat(d.pickupLat);
          if (d.pickupLng)      setPickupLng(d.pickupLng);
          if (d.dropoffLat)     setDropoffLat(d.dropoffLat);
          if (d.dropoffLng)     setDropoffLng(d.dropoffLng);
          // Only store rider-visible pricing fields
          if (d.totalCharged)   setPricing({ totalCharged: d.totalCharged, guestFee: d.guestFee || 0 });
          setTab("active");
        } else {
          setTab("past");
        }
      }).catch(() => setTab("past")).finally(() => setInitializing(false));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riderId]);

  /* init map */
  useEffect(() => {
    if (!mapReady || !tripId) return;
    const el = document.getElementById("tt-gmap");
    if (!el || mapRef.current) return;
    const g = (window as any).google.maps;
    mapRef.current = new g.Map(el, {
      center: { lat: pickupLat || -1.2921, lng: pickupLng || 36.8219 },
      zoom: 15, styles: LIGHT_MAP, disableDefaultUI: true,
      zoomControl: true, zoomControlOptions: { position: g.ControlPosition.RIGHT_CENTER },
    });
    if (pickupLat) {
      pickupMarkRef.current = new g.Marker({
        position: { lat: pickupLat, lng: pickupLng! }, map: mapRef.current,
        icon: { url: mkPickup(), scaledSize: new g.Size(28, 28), anchor: new g.Point(14, 14) }, zIndex: 10,
      });
    }
    if (dropoffLat) {
      dropMarkRef.current = new g.Marker({
        position: { lat: dropoffLat, lng: dropoffLng! }, map: mapRef.current,
        icon: { url: mkDropoff(), scaledSize: new g.Size(34, 44), anchor: new g.Point(17, 44) }, zIndex: 10,
      });
    }
  }, [mapReady, tripId, pickupLat, pickupLng, dropoffLat, dropoffLng]);

  /* animate car marker with lerp */
/* animate car marker — snaps to road path, rotates to road bearing */
  const animateCarTo = useCallback((lat: number, lng: number, heading: number) => {
    if (!mapRef.current || !mapReady) return;
    const g = (window as any).google.maps;

    const setCarAt = (posLat: number, posLng: number, posHdg: number) => {
   const iconUrl = buildCarIcon(posHdg, carImgUrlRef.current, 64);
if (!iconUrl) return;
const icon = { url: iconUrl, scaledSize: new g.Size(64, 64), anchor: new g.Point(32, 32) };
if (!carMarkerRef.current) {
        carMarkerRef.current = new g.Marker({
          position: { lat: posLat, lng: posLng }, map: mapRef.current,
          icon, zIndex: 20, optimized: false,
        });
      } else {
        carMarkerRef.current.setIcon(icon);
        carMarkerRef.current.setPosition({ lat: posLat, lng: posLng });
      }
      displayPosRef.current = { lat: posLat, lng: posLng };
    };

    // ── If we have a decoded road path, snap car onto it ──
    const path = routePathRef.current;
    if (path.length > 1) {
      // Build cumulative km distances along path
      const dists: number[] = [0];
      for (let i = 1; i < path.length; i++) {
        dists.push(dists[i - 1] + haversineKm(
          path[i - 1].lat(), path[i - 1].lng(),
          path[i].lat(),     path[i].lng()
        ));
      }
      const totalKm = dists[dists.length - 1];

      // Find closest point index to real GPS
      let closestIdx = 0, closestDist = Infinity;
      for (let i = 0; i < path.length; i++) {
        const d = haversineKm(lat, lng, path[i].lat(), path[i].lng());
        if (d < closestDist) { closestDist = d; closestIdx = i; }
      }
      const gpsFrac = dists[closestIdx] / totalKm;

      // Only advance forward — never snap backward on GPS jitter
      if (gpsFrac > carPctRef.current) carPctRef.current = gpsFrac;

      // Interpolate position & heading at carPctRef along path
      const targetKm = Math.min(carPctRef.current, 1) * totalKm;
      let seg = 0;
      for (let i = 1; i < dists.length; i++) {
        if (dists[i] >= targetKm) { seg = i - 1; break; }
        seg = i - 1;
      }
      const segLen = dists[seg + 1] - dists[seg];
      const frac   = segLen > 0 ? (targetKm - dists[seg]) / segLen : 0;
      const p1     = path[seg];
      const p2     = path[Math.min(seg + 1, path.length - 1)];
      const iLat   = p1.lat() + (p2.lat() - p1.lat()) * frac;
      const iLng   = p1.lng() + (p2.lng() - p1.lng()) * frac;
      const iHdg   = bearingDeg(p1.lat(), p1.lng(), p2.lat(), p2.lng());

      setCarAt(iLat, iLng, iHdg);
      return;
    }

    // ── Fallback: no route yet — lerp directly between GPS points ──
const iconUrl = buildCarIcon(heading, carImgUrlRef.current, 64);
if (!iconUrl) return;
const icon = { url: iconUrl, scaledSize: new g.Size(64, 64), anchor: new g.Point(32, 32) };
if (!carMarkerRef.current) {
  carMarkerRef.current = new g.Marker({
    position: { lat, lng }, map: mapRef.current,
    icon, zIndex: 20, optimized: false,
  });
  displayPosRef.current = { lat, lng }; return;
}
carMarkerRef.current.setIcon(icon);
    const start = displayPosRef.current || { lat, lng };
    const dLat = (lat - start.lat) / LERP_STEPS;
    const dLng = (lng - start.lng) / LERP_STEPS;
    if (lerpRef.current) clearInterval(lerpRef.current);
    let step = 0;
    lerpRef.current = setInterval(() => {
      step++;
      const la = start.lat + dLat * step, ln = start.lng + dLng * step;
      carMarkerRef.current?.setPosition({ lat: la, lng: ln });
      displayPosRef.current = { lat: la, lng: ln };
      if (step >= LERP_STEPS) { clearInterval(lerpRef.current!); lerpRef.current = null; }
    }, LERP_MS);
  }, [mapReady]);



  /* polylines */
const drawPolylines = useCallback((drLat: number, drLng: number, status: string) => {
  if (!mapRef.current || !mapReady) return;
  const g = (window as any).google.maps;

  // Determine origin & destination based on trip status
  const origin      = { lat: drLat, lng: drLng };
  const destination =
    status === "ongoing" && dropoffLat
      ? { lat: dropoffLat, lng: dropoffLng! }
      : pickupLat
      ? { lat: pickupLat,  lng: pickupLng!  }
      : null;

  if (!destination) {
    // Nothing to draw — just pan to driver
    mapRef.current.panTo(origin);
    mapRef.current.setZoom(16);
    return;
  }

  // De-duplicate: skip if origin/dest/status haven't meaningfully changed
  // (snap driver lat/lng to ~50 m grid so we don't re-request every GPS tick)
const snap  = (n: number) => Math.round(n * 10000) / 10000;
  const key   = `${status}|${snap(drLat)},${snap(drLng)}|${destination.lat},${destination.lng}`;
  if (key === lastRouteKeyRef.current) return;
  lastRouteKeyRef.current = key;

  // Init renderer once
  if (!directionsRendererRef.current) {
    directionsRendererRef.current = new g.DirectionsRenderer({
      suppressMarkers:   true,   // we draw our own car / pickup / dropoff icons
      preserveViewport:  true,   // we handle fitBounds ourselves
      polylineOptions: {
        strokeColor:   status === "ongoing" ? "#1B0E06" : "#D4900F",
        strokeWeight:  status === "ongoing" ? 5 : 3,
        strokeOpacity: status === "ongoing" ? 0.88 : 0,
        // dashed line for "coming to pickup" phase
        icons: status !== "ongoing" ? [{
          icon:   { path: "M 0,-1 0,1", strokeOpacity: 1, scale: 3, strokeColor: "#D4900F" },
          offset: "0",
          repeat: "14px",
        }] : [],
      },
    });
    directionsRendererRef.current.setMap(mapRef.current);
  } else {
    // Update line style when status changes between phases
    directionsRendererRef.current.setOptions({
      polylineOptions: {
        strokeColor:   status === "ongoing" ? "#1B0E06" : "#D4900F",
        strokeWeight:  status === "ongoing" ? 5 : 3,
        strokeOpacity: status === "ongoing" ? 0.88 : 0,
        icons: status !== "ongoing" ? [{
          icon:   { path: "M 0,-1 0,1", strokeOpacity: 1, scale: 3, strokeColor: "#D4900F" },
          offset: "0",
          repeat: "14px",
        }] : [],
      },
    });
  }

  const svc = new g.DirectionsService();
  svc.route(
    {
      origin,
      destination,
      travelMode: g.TravelMode.DRIVING,
      provideRouteAlternatives: false,
    },
    (result: any, routeStatus: any) => {
      if (routeStatus !== g.DirectionsStatus.OK) {
        // Directions failed (no API billing, etc.) — fall back to straight line
        polylinesRef.current.forEach(p => p.setMap(null));
        polylinesRef.current = [];
        polylinesRef.current.push(new g.Polyline({
          path: [origin, destination], map: mapRef.current,
          strokeColor: "#D4900F", strokeOpacity: 0, strokeWeight: 0,
          icons: [{ icon: { path: "M 0,-1 0,1", strokeOpacity: 1, scale: 3, strokeColor: "#D4900F" }, offset: "0", repeat: "14px" }],
          zIndex: 5,
        }));
        // fitBounds fallback
        const b = new g.LatLngBounds();
        [origin, destination].forEach(p => b.extend(p));
        mapRef.current.fitBounds(b, { top: 80, bottom: 260, left: 60, right: 60 });
        return;
      }

directionsRendererRef.current.setDirections(result);
      // Store decoded road path for on-road car animation
const newPath = result.routes[0].overview_path as any[];
const destChanged = routePathRef.current.length === 0 ||
  (newPath.length > 0 && routePathRef.current[routePathRef.current.length - 1]?.lat() !==
   newPath[newPath.length - 1]?.lat());
routePathRef.current = newPath;
if (destChanged) carPctRef.current = 0;
      // fitBounds around the actual route legs
      const bounds = new g.LatLngBounds();
      result.routes[0].legs[0].steps.forEach((step: any) => {
        bounds.extend(step.start_location);
        bounds.extend(step.end_location);
      });
if (destChanged) mapRef.current.fitBounds(bounds, { top: 80, bottom: 260, left: 60, right: 60 });    }
  );
}, [mapReady, pickupLat, pickupLng, dropoffLat, dropoffLng]);


useEffect(() => {
  return () => {
    directionsRendererRef.current?.setMap(null);
    directionsRendererRef.current = null;
    lastRouteKeyRef.current = "";
  };
}, []);


  /* react to GPS updates */
  useEffect(() => {
    if (!driverPos || SEARCHING.has(tripStatus)) return;
    const { lat, lng, heading = 0 } = driverPos;
    let h = heading || prevHeadingRef.current;
    if (prevGPSRef.current) {
      const d = haversineKm(prevGPSRef.current.lat, prevGPSRef.current.lng, lat, lng);
      if (d > 0.005) { h = bearingDeg(prevGPSRef.current.lat, prevGPSRef.current.lng, lat, lng); prevHeadingRef.current = h; }
    }
    prevGPSRef.current = { lat, lng };
    animateCarTo(lat, lng, h);
    drawPolylines(lat, lng, tripStatus);
  }, [driverPos, tripStatus, animateCarTo, drawPolylines]);

  useEffect(() => {
    if (carMarkerRef.current) carMarkerRef.current.setMap(SEARCHING.has(tripStatus) ? null : mapRef.current);
  }, [tripStatus]);

  /* sheet height */
  useEffect(() => {
    if (tripStatus === "completed" || tripStatus === "cancelled") setSheetMode("full");
    else if (tripStatus === "arriving") setSheetMode("full");
    else setSheetMode("half");
  }, [tripStatus]);

  /* arrival toast */
  useEffect(() => {
    if (tripStatus === "completed" && prevStatusRef.current !== "completed") {
      setShowToast(true); setTimeout(() => setShowToast(false), 5000);
    }
    prevStatusRef.current = tripStatus;
  }, [tripStatus]);

  /* RTDB: rider trip status */
  useEffect(() => {
    if (!tripId || !riderId) return;
    const r = dbRef(rtdb, `riderTrips/${riderId}/${tripId}`);
    onValue(r, snap => {
      const v = snap.val(); if (!v) return;
      if (v.status !== undefined)     setTripStatus(v.status);
      if (v.driverId)                 setDriverId(v.driverId);
      if (v.etaMinutes !== undefined) setEtaMinutes(v.etaMinutes);
    });
    return () => off(r, "value");
  }, [tripId, riderId]);

  /* RTDB: driver location via trip */
  useEffect(() => {
    if (!tripId) return;
    const r = dbRef(rtdb, `trips/${tripId}/driverLocation`);
    onValue(r, snap => {
      const v = snap.val(); if (!v?.lat || !v?.lng) return;
      setDriverPos(v);
      if (pickupLat) setEtaMinutes(Math.max(1, Math.round((haversineKm(v.lat, v.lng, pickupLat, pickupLng!) / 40) * 60)));
    });
    return () => off(r, "value");
  }, [tripId, pickupLat, pickupLng]);

  /* RTDB: onlineDrivers fallback */
  useEffect(() => {
    if (!driverId) return;
    const r = dbRef(rtdb, `onlineDrivers/${driverId}`);
    onValue(r, snap => { const v = snap.val(); if (!v?.lat || !v?.lng) return; setDriverPos(prev => prev ?? v); });
    return () => off(r, "value");
  }, [driverId]);

  /* ── Fetch driver info ──
     users/{driverId}                     → firstName, lastName, profilePicture, contactNumber
     Fleet where driverId == driverId     → brand, model, licensePlate, rating, rate, rateType, rideType
     (Fleet docs are keyed by a random listingId, not driverId — same
     collection/shape fleetSlice.ts's parseDriver() and RideBookings/DriverCars
     already read — so this has to be a query, not a direct doc(db,...) get.)
  ── */
  useEffect(() => {
    if (!driverId) return;
    // Public profile — users collection
    getDoc(doc(db, "users", driverId))
      .then(s => { if (s.exists()) setDriverUserDoc(s.data() as DriverUserDoc); })
      .catch(console.warn);
    // Vehicle / listing — Fleet collection, this driver's currently selected vehicle
    const q = query(
      collection(db, "Fleet"),
      where("driverId", "==", driverId),
      where("isSelected", "==", true),
      limit(1)
    );
    getDocs(q)
      .then(snap => { if (!snap.empty) setDriverListingDoc(snap.docs[0].data() as DriverListingDoc); })
      .catch(console.warn);
  }, [driverId]);

  /* Firestore: hydrate trip doc */
  useEffect(() => {
    if (!tripId) return;
    import("firebase/firestore").then(({ doc: fsDoc, getDoc: fsGetDoc }) => {
      fsGetDoc(fsDoc(db, "rideTrips", tripId)).then(snap => {
        if (!snap.exists()) return;
        const d = snap.data() as any;
        if (d.status)        setTripStatus(d.status);
        if (d.driverId)      setDriverId(d.driverId);
        if (d.etaMinutes)    setEtaMinutes(d.etaMinutes);
        // ONLY rider-visible fields — never driverProfit / hostFee
        if (d.totalCharged)  setPricing({ totalCharged: d.totalCharged, guestFee: d.guestFee || 0 });
        if (d.pickupAddress  && !initPickupAddress)  setPickupAddress(d.pickupAddress);
        if (d.dropoffAddress && !initDropoffAddress) setDropoffAddress(d.dropoffAddress);
        if (d.pickupLat  && !initPickupLat)  setPickupLat(d.pickupLat);
        if (d.pickupLng  && !initPickupLng)  setPickupLng(d.pickupLng);
        if (d.dropoffLat && !initDropoffLat) setDropoffLat(d.dropoffLat);
        if (d.dropoffLng && !initDropoffLng) setDropoffLng(d.dropoffLng);
      }).catch(console.warn);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]);

  /* ── Fetch ALL past trips for this rider ── */
  const fetchPast = useCallback(async () => {
    if (!riderId) return;
    setLoadingPast(true);
    try {
      const { collection, query, where, orderBy, getDocs } = await import("firebase/firestore");
      const q = query(
        collection(db, "rideTrips"),
        where("riderId", "==", riderId),
        orderBy("createdAt", "desc")
      );
      const snap = await getDocs(q);
      setPastTrips(snap.docs.map(d => ({
        id: d.id, tripId: (d.data() as any).tripId || d.id, ...d.data(),
      })) as PastTrip[]);
    } catch {
      // Fallback without orderBy (avoids composite index requirement)
      try {
        const { collection, query, where, getDocs } = await import("firebase/firestore");
        const snap = await getDocs(query(collection(db, "rideTrips"), where("riderId", "==", riderId)));
        setPastTrips(
          snap.docs
            .map(d => ({ id: d.id, tripId: (d.data() as any).tripId || d.id, ...d.data() } as PastTrip))
            .sort((a: any, b: any) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0))
        );
      } catch (e2) { console.warn("fetchPast:", e2); }
    } finally { setLoadingPast(false); }
  }, [riderId]);

  useEffect(() => { if (tab === "past") fetchPast(); }, [tab, fetchPast]);

  /* cancel */
  const handleCancel = useCallback(async () => {
    if (!tripId || cancelling || !window.confirm("Cancel this ride?")) return;
    setCancelling(true);
    try {
      const res = await fetch("https://europe-west3-heartlaas.cloudfunctions.net/cancelRide", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tripId, cancelledBy: riderId, reason: "rider_cancelled" }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error?.message || "Failed");
      setTripStatus("cancelled");
    } catch (e: any) { alert(e.message); }
    finally { setCancelling(false); }
  }, [tripId, riderId, cancelling]);

  /* ── Rebook:
     - Fetch fresh rate/rateType from riders/{driverId}
     - Fetch driver name/photo from users/{driverId}
     - Verify driver is online in RTDB
     - Navigate to /Rebook with full payload
  ── */
  const handleRebook = useCallback(async (trip: PastTrip) => {
    const dId = trip.driverId;
    if (!dId) { alert("No driver info for this trip."); return; }

    const tKey = trip.tripId || trip.id || "";
    setRebookingId(tKey);

    try {
      // 1. Fetch fresh driver data — Fleet is queried by driverId field
      //    (docs are keyed by listingId, not driverId), users is a direct get.
      const [listingQuerySnap, userSnap] = await Promise.all([
        getDocs(query(
          collection(db, "Fleet"),
          where("driverId", "==", dId),
          where("isSelected", "==", true),
          limit(1)
        )),
        getDoc(doc(db, "users", dId)),
      ]);
      const listing = !listingQuerySnap.empty ? (listingQuerySnap.docs[0].data() as DriverListingDoc) : null;
      const user    = userSnap.exists() ? (userSnap.data() as DriverUserDoc) : null;

      // 2. Verify driver online & available
      const driverLoc = await new Promise<any>(resolve => {
        const r = dbRef(rtdb, `onlineDrivers/${dId}`);
        onValue(r, snap => { off(r, "value"); resolve(snap.val()); }, { onlyOnce: true });
      });
      if (!driverLoc?.isAvailable) {
        alert("This driver is not available right now. Try again later.");
        return;
      }

      // 3. Compute pickup ETA
      const rebookPickupLat = trip.pickupLat ?? pickupLat;
      const rebookPickupLng = trip.pickupLng ?? pickupLng;
      let pickupDistKm = 0, etaMin: number | null = null;
      if (rebookPickupLat && rebookPickupLng) {
        pickupDistKm = haversineKm(driverLoc.lat, driverLoc.lng, rebookPickupLat, rebookPickupLng);
        etaMin       = Math.max(1, Math.round((pickupDistKm / 40) * 60));
      }

      // 4. Build driver display name
      const driverName = user?.firstName
        ? `${user.firstName}${user.lastName ? " " + user.lastName : ""}`
        : listing?.fullName || listing?.driverName || "";

      // 5. Navigate to /Rebook
      navigate("/Rebook", {
        state: {
          listingId:      trip.listingId || dId,
          driverId:       dId,
          riderId,

          pickupLat:      rebookPickupLat,
          pickupLng:      rebookPickupLng,
          pickupAddress:  trip.pickupAddress || pickupAddress || "",

          dropoffLat:     trip.dropoffLat ?? null,
          dropoffLng:     trip.dropoffLng ?? null,
          dropoffAddress: trip.dropoffAddress || "",

          driverLat:      driverLoc.lat,
          driverLng:      driverLoc.lng,
          pickupDistKm,
          etaMinutes:     etaMin,

          // Always use fresh DB rate — never stale trip values
          rate:           listing?.rate     ?? trip.rate     ?? 0,
          rateType:       listing?.rateType ?? trip.rateType ?? "perKm",
          currency:       listing?.currency ?? trip.currency ?? "eur",

          // Driver display — from users collection
          driverName,
          driverPhoto:    user?.profilePicture  || user?.profilePhoto || null,
          driverPhone:    user?.contactNumber   || user?.phone        || null,

          // Vehicle info — from riders collection
          driverBrand:    listing?.brand        || "",
          driverModel:    listing?.model        || "",
          driverPlate:    listing?.licensePlate || "",
          driverRating:   listing?.rating       ?? null,

          previousTripId: trip.tripId || trip.id,
        },
      });
    } catch (e: any) {
      console.error("Rebook error:", e);
      alert(e.message || "Could not start rebook. Please try again.");
    } finally {
      setRebookingId(null);
    }
  }, [riderId, pickupLat, pickupLng, pickupAddress, navigate]);

  /* ── Derived ── */
  const sc            = SC[tripStatus] || SC.requested;
  const SIcon         = sc.Icon;
  const isTerminal    = TERMINAL.has(tripStatus);
  const isCancellable = CANCELLABLE.has(tripStatus);
  const isSearching   = SEARCHING.has(tripStatus);
  const showSteps     = !["cancelled", "no_driver"].includes(tripStatus);

  // Driver display name — users collection firstName first
  const driverDisplayName =
    driverUserDoc?.firstName
      ? `${driverUserDoc.firstName}${driverUserDoc.lastName ? " " + driverUserDoc.lastName : ""}`
      : driverUserDoc?.fullName
      || driverListingDoc?.fullName
      || driverListingDoc?.driverName
      || "Your Driver";

  const driverPhoto = driverUserDoc?.profilePicture || driverUserDoc?.profilePhoto || null;
  const driverPhone = driverUserDoc?.contactNumber  || driverUserDoc?.phone        || null;
  const hasDriverInfo = !!(driverUserDoc || driverListingDoc);

  /* ── Panel content (active trip sheet) ───────────────────────────────── */
  const PanelContent = () => (
    <>
      {showSteps && (
        <div className="tt-dashes">
          {STATUS_STEPS.map(step => (
            <div key={step.key} className={`tt-dash ${getStepState(step.key, tripStatus)}`} />
          ))}
        </div>
      )}

      {showSteps && (
        <div className="tt-narr">
          {STATUS_STEPS.map(step => {
            const state = getStepState(step.key, tripStatus);
            const Ic = SC[step.key]?.Icon || Car;
            return (
              <div key={step.key} className="tt-narr-step">
                <div className={`tt-narr-dot ${state}`}>
                  {state === "done"    && <CheckCircle size={12} />}
                  {state === "active"  && <Ic size={12} />}
                  {state === "pending" && <div style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--bord)" }} />}
                </div>
                <div style={{ flex: 1, paddingTop: 4 }}>
                  <div className={`tt-narr-label ${state}`}>{step.label}</div>
                  {state === "active" && <div className="tt-narr-sub">{step.sub}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Driver card — rider-facing only, absolutely no earnings */}
      {hasDriverInfo && !isSearching && (
        <div className="tt-drv">
          <div className="tt-drv-row">
            <div className="tt-drv-av-wrap">
              {driverPhoto
                ? <img src={driverPhoto} alt={driverDisplayName} className="tt-drv-av" onError={(e) => { (e.currentTarget as HTMLImageElement).src = appLogo; }} />
                : <img src={appLogo} alt={driverDisplayName} className="tt-drv-av" style={{ objectFit: "contain", background: "#fff", padding: 8 }} />
              }
              <div className="tt-drv-online" />
            </div>
            <div className="tt-drv-info">
              <div className="tt-drv-name">{driverDisplayName}</div>
              {(driverListingDoc?.brand || driverListingDoc?.model) && (
                <div className="tt-drv-veh">
                  {[driverListingDoc?.brand, driverListingDoc?.model].filter(Boolean).join(" ")}
                </div>
              )}
              {driverListingDoc?.licensePlate && (
                <div className="tt-drv-plate">{driverListingDoc.licensePlate}</div>
              )}
            </div>
            {driverListingDoc?.rating != null && (
              <div className="tt-drv-star">
                <Star size={10} fill="#F2A93E" color="#F2A93E" />
                <span>{Number(driverListingDoc.rating).toFixed(1)}</span>
              </div>
            )}
          </div>
          <div className="tt-drv-acts">
            {driverPhone && (
              <button className="tt-drv-act ph" onClick={() => window.open(`tel:${driverPhone}`)}>
                <Phone size={13} /> {driverPhone}
              </button>
            )}
            <button className="tt-drv-act ms" onClick={() => { if (driverId) navigate(`/messages/${driverId}`); }}>
              <MessageCircle size={13} /> Message
            </button>
          </div>
        </div>
      )}

      {/* Route */}
      <div className="tt-route">
        <div className="tt-route-row">
          <div className="tt-route-dots">
            <div className="tt-route-dot" style={{ borderColor: "#1B0E06", background: "#1B0E06" }} />
            <div className="tt-route-vline" />
          </div>
          <div className="tt-route-body">
            <div className="tt-route-lbl">Pickup</div>
            <div className="tt-route-addr">{pickupAddress || "Pickup location"}</div>
          </div>
        </div>
        <div className="tt-route-row">
          <div className="tt-route-dots">
            <div className="tt-route-dot" style={{ borderColor: "#C8202E", background: "#C8202E" }} />
          </div>
          <div className="tt-route-body">
            <div className="tt-route-lbl">Dropoff</div>
            <div className="tt-route-addr">{dropoffAddress || "Destination"}</div>
          </div>
        </div>
      </div>

      {/* Price — total paid + service fee ONLY. Never driver profit or host fee. */}
      {(pricing?.totalCharged > 0) && (
        <div className="tt-price">
          <div className="tt-pi-item">
            <div className="tt-pi-lbl">Total Paid</div>
            <div className="tt-pi-val">€{Number(pricing.totalCharged).toFixed(2)}</div>
          </div>
          {pricing.guestFee > 0 && (
            <>
              <div className="tt-pi-div" />
              <div className="tt-pi-item">
                <div className="tt-pi-lbl">Service Fee</div>
                <div className="tt-pi-val">€{Number(pricing.guestFee).toFixed(2)}</div>
              </div>
            </>
          )}
        </div>
      )}

      {isCancellable && (
        <button className="tt-cancel" onClick={handleCancel} disabled={cancelling}>
          {cancelling ? <><Loader2 size={14} className="spin" /> Cancelling…</> : <><XCircle size={14} /> Cancel Ride</>}
        </button>
      )}

      {isTerminal && (
        <div className="tt-done-row">
          <button className="tt-done-btn" onClick={() => navigate("/")}>
            {tripStatus === "completed" ? "Done" : "Go Back"} <ArrowRight size={14} />
          </button>
          {tripStatus === "completed" && (
            <button className="tt-hist-btn" onClick={() => setTab("past")}>
              <Clock size={13} /> History
            </button>
          )}
        </div>
      )}
    </>
  );

  /* ── Past trips ───────────────────────────────────────────────────────── */
  const PastTripsView = () => {
    if (loadingPast) return (
      <div className="tt-loader">
        <div className="tt-loader-sp" />
        <div style={{ fontSize: 12, color: "var(--muted)" }}>Loading trips…</div>
      </div>
    );

    if (!pastTrips.length) return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "64px 32px", textAlign: "center", gap: 12 }}>
        <Clock size={52} color="var(--bord)" />
        <div style={{ fontFamily: "var(--serif)", fontSize: 26, fontWeight: 800, color: "var(--blk)", marginTop: 8 }}>No Trips Yet</div>
        <div style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.7, maxWidth: 250 }}>Your completed trips will appear here.</div>
        <button className="tt-book-btn" onClick={() => navigate("/riderhome")}>
          <Car size={13} /> Book a Ride <ArrowRight size={13} />
        </button>
      </div>
    );

    return (
      <div className="tt-past-list">
        <div className="tt-past-hdr">
          <div className="tt-past-title">Ride History</div>
          <div className="tt-past-sub">{pastTrips.length} trip{pastTrips.length !== 1 ? "s" : ""}</div>
        </div>

        {pastTrips.map((trip, i) => {
          const tsc  = SC[trip.status] || SC.completed;
          const tKey = trip.tripId || trip.id || String(i);
          const isRebooking = rebookingId === tKey;

          return (
            <div key={tKey} className="tt-trip-card" style={{ "--d": `${i * 0.045}s` } as any}>
              {/* coloured accent bar */}
              <div className="tt-trip-accent" style={{ background: tsc.color }} />

              <div className="tt-trip-body">
                {/* status pill + date */}
                <div className="tt-trip-top">
                  <div
                    className="tt-trip-pill"
                    style={{ borderColor: tsc.color + "55", background: tsc.color + "0e", color: tsc.color }}
                  >
                    <tsc.Icon size={10} />{tsc.label}
                  </div>
                  <span className="tt-trip-date">{fmtDate(trip.createdAt)}</span>
                </div>

                {/* Pickup → Dropoff route */}
                <div className="tt-trip-route">
                  <div className="tt-trip-route-row">
                    <div className="tt-trip-route-dot" style={{ background: "#1B0E06" }} />
                    <div className="tt-trip-route-body">
                      <div className="tt-trip-route-lbl">From</div>
                      <div className="tt-trip-route-addr">{trip.pickupAddress || "Pickup location"}</div>
                    </div>
                  </div>
                  <div style={{ width: 1, height: 10, background: "var(--bord)", margin: "3px 0 3px 3.5px" }} />
                  <div className="tt-trip-route-row">
                    <div className="tt-trip-route-dot" style={{ background: "#C8202E" }} />
                    <div className="tt-trip-route-body">
                      <div className="tt-trip-route-lbl">To</div>
                      <div className="tt-trip-route-addr">{trip.dropoffAddress || "Destination"}</div>
                    </div>
                  </div>
                </div>

                {/* Stats — distance + total paid ONLY, never driver breakdown */}
                <div className="tt-trip-stats">
                  {(trip.distanceKm || 0) > 0 && (
                    <span className="tt-trip-stat"><Navigation size={10} />{trip.distanceKm} km</span>
                  )}
                  {(trip.totalCharged || 0) > 0 && (
                    <span className="tt-trip-stat"><CreditCard size={10} />€{Number(trip.totalCharged).toFixed(2)}</span>
                  )}
                  {trip.rateType && (
                    <span className="tt-trip-stat"><Car size={10} />{trip.rateType}</span>
                  )}
                </div>

                {/* Rebook button — completed trips only */}
                {trip.status === "completed" && (
                  <button
                    className="tt-rebook"
                    disabled={isRebooking}
                    onClick={() => handleRebook(trip)}
                  >
                    {isRebooking
                      ? <><Loader2 size={11} className="spin" /> Checking driver…</>
                      : <><RotateCcw size={11} /> Rebook This Ride</>}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  /* ── Render ───────────────────────────────────────────────────────────── */
  return (
    <div className="tt">
      <header className="tt-hdr">
        <div className="tt-logo" onClick={() => navigate("/")}>
          <div className="tt-logo-sq"><Heart size={13} color="#fff" fill="#fff" /></div>
          <span className="tt-logo-name">HEARTLAS</span>
        </div>
        <div className="tt-hdiv" />
        <span className="tt-hdr-title">{tab === "active" ? (tripId ? sc.label : "My Rides") : "History"}</span>
        {tripId && !isTerminal && tab === "active" && (
          <div className="tt-live-pill">
            <div className="tt-live-dot" /><span className="tt-live-txt">LIVE</span>
          </div>
        )}
        <button className="tt-icon-btn" onClick={() => navigate(-1)}><ChevronLeft size={17} /></button>
      </header>

      <div className="tt-tabbar">
        {(["active", "past"] as const).map(t => (
          <button key={t} className={`tt-tab${tab === t ? " tt-tab-on" : ""}`} onClick={() => setTab(t)}>
            {t === "active" ? <Navigation size={12} /> : <Clock size={12} />}
            {t === "active" ? "Active Ride" : "History"}
            {t === "active" && tripId && !isTerminal && <div className="tt-tab-pip" />}
          </button>
        ))}
      </div>

      <div style={{ position: "absolute", inset: "98px 0 0 0", overflow: "hidden" }}>
        {tab === "active" ? (
          initializing ? (
            <div className="tt-empty-screen" style={{ top: 0 }}>
              <Loader2 size={36} color="#C8202E" className="spin" style={{ marginBottom: 12 }} />
              <div style={{ fontSize: 13, color: "var(--muted)" }}>Loading your trip…</div>
            </div>
          ) : !tripId ? (
            <div className="tt-empty-screen" style={{ top: 0 }}>
              <Car size={60} color="var(--bord)" style={{ marginBottom: 8 }} />
              <div className="tt-empty-title">No Active Ride</div>
              <div className="tt-empty-sub">Book a ride to track your driver live on the map.</div>
              <button className="tt-book-btn" onClick={() => navigate("/riderhome")}>
                <Car size={13} /> Book a Ride <ArrowRight size={13} />
              </button>
            </div>
          ) : (
            <div style={{ position: "relative", width: "100%", height: "100%" }}>
              {mapReady
                ? <div id="tt-gmap" style={{ position: "absolute", inset: 0 }} />
                : <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "var(--off)" }}><Loader2 size={28} color="#C8202E" className="spin" /></div>
              }

              {isSearching && (
                <div className="tt-searching-ov">
                  <div className="tt-srch-orb"><Radio size={30} /></div>
                  <div className="tt-srch-lbl">Finding your driver</div>
                  <div className="tt-srch-sub">Matching with the nearest available driver…</div>
                </div>
              )}

              {/* Desktop sidebar */}
              <div className="tt-sidebar">
                <div className="tt-sb-status">
                  <div className="tt-sb-status-row">
                    <div className="tt-sb-icon" style={{ background: sc.bg }}><SIcon size={21} color={sc.color} /></div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: "var(--serif)", fontSize: 17, fontWeight: 800, color: "var(--blk)", lineHeight: 1.2 }}>{sc.headline}</div>
                      <div style={{ fontSize: 11, color: "var(--smoke)", marginTop: 3 }}>{sc.sub}</div>
                    </div>
                    {etaMinutes != null && !isTerminal && (
                      <div className="tt-eta" style={{ padding: "7px 12px" }}>
                        <div className="tt-eta-num" style={{ fontSize: 20 }}>{etaMinutes}</div>
                        <div className="tt-eta-unit">MIN</div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="tt-sb-body"><PanelContent /></div>
              </div>

              {/* Mobile bottom sheet */}
              <div className={`tt-sheet ${sheetMode}`}>
                <div className="tt-sh-handle" onClick={() => setSheetMode(m => m === "peek" ? "half" : m === "half" ? "full" : "peek")}>
                  <div className="tt-sh-pill" />
                </div>
                <div className="tt-sh-status">
                  <div className="tt-sh-icon" style={{ background: sc.bg }}><SIcon size={20} color={sc.color} /></div>
                  <div className="tt-sh-text">
                    <div className="tt-sh-headline">{sc.headline}</div>
                    <div className="tt-sh-sub">{sc.sub}</div>
                  </div>
                  {etaMinutes != null && !isTerminal && (
                    <div className="tt-eta">
                      <div className="tt-eta-num">{etaMinutes}</div>
                      <div className="tt-eta-unit">MIN</div>
                    </div>
                  )}
                  <div className="tt-sh-tog" onClick={() => setSheetMode(m => m === "full" ? "half" : "full")}>
                    {sheetMode === "full" ? <ChevronDown size={17} /> : <ChevronUp size={17} />}
                  </div>
                </div>
                <div className="tt-sh-body"><PanelContent /></div>
              </div>
            </div>
          )
        ) : (
          <div className="tt-past-screen" style={{ top: 0 }}><PastTripsView /></div>
        )}
      </div>

      <div className={`tt-toast${showToast ? " show" : ""}`}>
        <CheckCircle size={17} color="#1A9E6E" />
        You've arrived! Thanks for riding with Heartlas 🎉
      </div>
    </div>
  );
}